/**
 * Draft and save payload for the job workspace edit panel.
 * Only fields the user changes are sent, so a save cannot wipe unrelated job data.
 */

/**
 * `datetime-local` value in UTC, matching the workspace clock.
 * @param {unknown} value
 * @returns {string}
 */
export function toUtcDateTimeLocal(value) {
  if (value == null || value === "") return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

/**
 * Convert a UTC `datetime-local` value back to an ISO instant.
 * @param {unknown} value
 * @returns {string|null}
 */
export function fromUtcDateTimeLocal(value) {
  if (!value) return null;
  const text = String(value);
  const normalized = text.length === 16 ? `${text}:00.000Z` : text;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * Customer reference shown on the workspace, including the fallbacks the card uses.
 * @param {object|null|undefined} job
 * @returns {string}
 */
function displayedCustomerReference(job) {
  const metadata = job?.metadata || {};
  return String(
    metadata.customerReference ||
      metadata.customerPO ||
      metadata.poNumber ||
      metadata.reference ||
      ""
  );
}

/**
 * Commodity shown on the workspace, including a leg shipping fallback.
 * @param {object|null|undefined} job
 * @returns {string}
 */
function displayedCommodity(job) {
  if (job?.metadata?.commodity) return String(job.metadata.commodity);
  const legs = Array.isArray(job?.legs) ? job.legs : [];
  for (const leg of legs) {
    if (leg?.shipping?.commodity) return String(leg.shipping.commodity);
    if (leg?.shipping?.description) return String(leg.shipping.description);
  }
  return "";
}

/**
 * Editable fields for one job, prefilled with what the workspace is showing.
 * @param {object|null|undefined} job
 * @returns {{ note: string, customerReference: string, commodity: string, appointmentDate: string }}
 */
export function buildJobEditDraft(job) {
  return {
    note: job?.note ? String(job.note) : "",
    customerReference: displayedCustomerReference(job),
    commodity: displayedCommodity(job),
    appointmentDate: toUtcDateTimeLocal(job?.appointmentDate),
  };
}

/**
 * Job update body. Empty when the draft still matches the job.
 * @param {{ note?: string, customerReference?: string, commodity?: string, appointmentDate?: string }} draft
 * @param {object|null|undefined} job
 * @returns {object}
 */
export function buildJobEditPayload(draft, job) {
  const baseline = buildJobEditDraft(job);
  const payload = {};
  const note = draft?.note ?? "";

  if (note !== baseline.note) {
    payload.note = note;
  }

  const customerReference = String(draft?.customerReference ?? "").trim();
  const commodity = String(draft?.commodity ?? "").trim();
  if (
    customerReference !== baseline.customerReference ||
    commodity !== baseline.commodity
  ) {
    payload.metadata = { ...(job?.metadata || {}) };
    if (customerReference !== baseline.customerReference) {
      payload.metadata.customerReference = customerReference;
    }
    if (commodity !== baseline.commodity) {
      payload.metadata.commodity = commodity;
    }
  }

  const appointmentDate = draft?.appointmentDate ?? "";
  if (appointmentDate !== baseline.appointmentDate) {
    payload.appointmentDate = fromUtcDateTimeLocal(appointmentDate);
  }

  return payload;
}
